import { Button, DatePicker, message, Segmented, Switch, Tooltip } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutSite } from '../components/layout';
import { Loader } from '../components/loader';
import { get_calpin, get_meeting_list, get_skyracing } from '../services/api.service';
import { CalpinMeeting, formatdateYYYYMMDD, MLMeeting, MLRace, SkyRacingMeeting } from '../types';
import './week.scss';
import { useLocalStorage } from '../localstorage';
import { LeftCircleFilled, RightCircleFilled } from '@ant-design/icons';

const HOUR_HEIGHT = 48;       // px per hour
const DAY_MAX_WIDTH = 200;      // max race width in day mode (px)
const RACE_DURATION = 20;       // displayed race duration (minutes)
const CALPIN_RACE_DURATION = 35;    // calpin meeting duration per race (minutes)
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/* -------------------------------------------------------------------------------- *\
|                               WeekRace
\* -------------------------------------------------------------------------------- */
interface WeekRace
{
    race: MLRace;
    start: number;      // minutes since midnight (local time)
    end: number;
    column: number;     // column index
    columns: number;    // number of columns of the day
}
/* -------------------------------------------------------------------------------- *\
|                               WeekPlanned
|   planned meeting (calpin, sky racing) displayed in background
\* -------------------------------------------------------------------------------- */
interface WeekPlanned
{
    key: string;
    sic: string;
    label: string;
    tooltip: ReactNode;
    start: number;      // minutes since midnight (local time)
    end: number;
    column: number;
    columns: number;
}

/* -------------------------------------------------------------------------------- *\
|                               startOfWeek (monday)
\* -------------------------------------------------------------------------------- */
const startOfWeek = (d: Dayjs): Dayjs => d.startOf('day').subtract((d.day() + 6) % 7, 'day');

/* -------------------------------------------------------------------------------- *\
|                               sicColor
\* -------------------------------------------------------------------------------- */
const sicColor = (sic: string): string =>
{
    let h = 0;
    for (let i = 0; i < sic.length; i++) h = (h * 31 + sic.charCodeAt(i)) % 360;
    return `hsl(${h}, 45%, 38%)`;
}

/* -------------------------------------------------------------------------------- *\
|                               layoutDay
|   one column per meeting; column width is based on the maximum
|   number of simultaneous meetings of the day.
|   a planned meeting shares the column of the races of the same sic
\* -------------------------------------------------------------------------------- */
const layoutDay = (races: WeekRace[], planned: WeekPlanned[]): void =>
{
    races.sort((a, b) => a.start - b.start || a.race.sic.localeCompare(b.race.sic));

    // time span of each meeting in the day (races + planned)
    const meetings = new Map<string, { start: number, end: number }>();
    const addspan = (sic: string, start: number, end: number) =>
    {
        const m = meetings.get(sic);
        if (!m) meetings.set(sic, { start, end });
        else
        {
            m.start = Math.min(m.start, start);
            m.end = Math.max(m.end, end);
        }
    }
    races.forEach((r) => addspan(r.race.sic, r.start, r.end));
    planned.forEach((p) => addspan(p.sic, p.start, p.end));

    // one column per meeting, reused once a meeting is over
    const colEnds: number[] = [];
    const sicCols = new Map<string, number>();
    Array.from(meetings.entries())
        .sort((a, b) => a[1].start - b[1].start || a[0].localeCompare(b[0]))
        .forEach(([sic, m]) =>
        {
            let col = colEnds.findIndex((e) => e <= m.start);
            if (col < 0)
            {
                col = colEnds.length;
                colEnds.push(m.end);
            }
            else
            {
                colEnds[col] = m.end;
            }
            sicCols.set(sic, col);
        });

    races.forEach((r) =>
    {
        r.column = sicCols.get(r.race.sic) ?? 0;
        r.columns = colEnds.length;
    });
    planned.forEach((p) =>
    {
        p.column = sicCols.get(p.sic) ?? 0;
        p.columns = colEnds.length;
    });
}

/* -------------------------------------------------------------------------------- *\
|                               WeekPage
\* -------------------------------------------------------------------------------- */
export const WeekPage: FC<{ children?: ReactNode }> = ({ children }) =>
{
    const [lastselecteddate, setlastselecteddate] = useLocalStorage('lastselecteddate', formatdateYYYYMMDD(new Date()));
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useLocalStorage('weekmode', 'week');     // 'week' | 'day'
    const [weekstart, setWeekstart] = useState<Dayjs>(startOfWeek(dayjs(lastselecteddate)));
    const [days, setDays] = useState<WeekRace[][]>([[], [], [], [], [], [], []]);
    const [calpin, setCalpin] = useState<CalpinMeeting[]>([]);
    const [showcalpin, setShowcalpin] = useLocalStorage('weekcalpin', 'true');
    const [skyracing, setSkyracing] = useState<SkyRacingMeeting[]>([]);
    const [showskyracing, setShowskyracing] = useLocalStorage('weekskyracing', 'true');
    const [now, setNow] = useState<Dayjs>(dayjs());
    const gridRef = useRef<HTMLDivElement>(null);
    const hoverRef = useRef<HTMLDivElement>(null);

    const navigate = useNavigate();

    /* -------------------------------------------------------------------------------- *\
    |                               refreshWeek
    \* -------------------------------------------------------------------------------- */
    const refreshWeek = async (start: Dayjs) =>
    {
        const dates = DAY_NAMES.map((_, i) => start.add(i, 'day'));
        // calpin / sky racing are optional: an error must not prevent the display of the races
        get_calpin().then((f) => setCalpin(f.meetings || [])).catch(() => setCalpin([]));
        get_skyracing().then((f) => setSkyracing(f.meetings || [])).catch(() => setSkyracing([]));
        try
        {
            const results = await Promise.all(dates.map((d) => get_meeting_list(d.format('YYYYMMDD'))));
            const newdays: WeekRace[][] = [[], [], [], [], [], [], []];
            results.forEach((meetings: MLMeeting[], i: number) =>
            {
                meetings.forEach((m: MLMeeting) => m.races.forEach((race: MLRace) =>
                {
                    const date = dayjs(race.date);
                    if (!date.isValid()) return;
                    // column = local day of the race if within the week, otherwise the meeting day
                    const diff = date.startOf('day').diff(start, 'day');
                    const dayindex = diff >= 0 && diff < 7 ? diff : i;
                    const minutes = date.hour() * 60 + date.minute();
                    newdays[dayindex].push({ race, start: minutes, end: Math.min(minutes + RACE_DURATION, 24 * 60), column: 0, columns: 1 });
                }));
            });
            setDays(newdays);
        }
        catch (error: any)
        {
            message.error(error.message);
            if (error.status === 403 || error.status === 401) navigate('/login');
        }
    }
    /* -------------------------------------------------------------------------------- *\
    |                               changeDate
    |   the week is reloaded only when it changes
    \* -------------------------------------------------------------------------------- */
    const changeDate = (date: Dayjs) =>
    {
        const start = startOfWeek(date);
        if (!start.isSame(weekstart, 'day')) setWeekstart(start);
        setlastselecteddate(date.format('YYYYMMDD'));
    }
    /* -------------------------------------------------------------------------------- *\
    |                               openRace
    \* -------------------------------------------------------------------------------- */
    const openRace = (race: MLRace) =>
    {
        const meetingdate = new Date(race.meetingdate ? race.meetingdate : race.date);
        if (!isNaN(meetingdate.getTime())) navigate('/race/' + formatdateYYYYMMDD(meetingdate) + '/' + race.sic + '/' + race.number);
    }

    useEffect(() =>
    {
        setLoading(true);
        refreshWeek(weekstart).then(() => setLoading(false));
        // eslint-disable-next-line
    }, [weekstart])

    useEffect(() =>
    {
        const interval = setInterval(() => setNow(dayjs()), 60000);
        return () => clearInterval(interval);
    }, []);

    /* -------------------------------------------------------------------------------- *\
    |                               planneddays
    |   calpin / sky racing meetings of the displayed week, laid out with the races
    \* -------------------------------------------------------------------------------- */
    const planneddays = useMemo((): WeekPlanned[][] =>
    {
        const result: WeekPlanned[][] = [[], [], [], [], [], [], []];
        // start / end = ISO dates, the meeting is displayed on its local start day (capped at midnight)
        const add = (item: Pick<WeekPlanned, 'key' | 'sic' | 'label' | 'tooltip'>, start: string, end: Dayjs) =>
        {
            const date = dayjs(start);
            if (!date.isValid()) return;
            const dayindex = date.startOf('day').diff(weekstart, 'day');
            if (dayindex < 0 || dayindex >= 7) return;
            const minutes = date.hour() * 60 + date.minute();
            const endminutes = Math.min(end.diff(date.startOf('day'), 'minute'), 24 * 60);
            result[dayindex].push({ ...item, start: minutes, end: Math.max(endminutes, minutes + 1), column: 0, columns: 1 });
        }
        if (showcalpin === 'true')
        {
            calpin.forEach((meeting: CalpinMeeting) =>
            {
                const start = dayjs(meeting.start);
                add({
                    key: 'calpin_' + meeting.sic + '_' + meeting.start,
                    sic: meeting.sic,
                    label: start.format('HH:mm') + ' ' + meeting.sic + ' R' + meeting.number + ' – ' + meeting.races + ' races',
                    tooltip:
                        <div>
                            <div><b>{meeting.sic} R{meeting.number}</b> – {start.format('HH:mm')} (calpin)</div>
                            <div>{meeting.name}</div>
                            <div>{meeting.races} races – {meeting.specialty} – {meeting.status}</div>
                        </div>,
                }, meeting.start, start.add(Math.max(meeting.races, 1) * CALPIN_RACE_DURATION, 'minute'));
            });
        }
        if (showskyracing === 'true')
        {
            skyracing.forEach((meeting: SkyRacingMeeting) =>
            {
                const start = dayjs(meeting.start);
                const last = meeting.times.length ? meeting.times[meeting.times.length - 1].time : meeting.start;
                add({
                    key: 'skyracing_' + meeting.sic + '_' + meeting.start,
                    sic: meeting.sic,
                    label: start.format('HH:mm') + ' ' + meeting.sic + ' – ' + meeting.races + ' races',
                    tooltip:
                        <div>
                            <div><b>{meeting.sic}</b> – {start.format('HH:mm')} (sky racing)</div>
                            <div>{meeting.name} – {meeting.type} – {meeting.races} races</div>
                            <div>{meeting.times.map((t) => 'C' + t.number + ' ' + dayjs(t.time).format('HH:mm')).join(', ')}</div>
                        </div>,
                }, meeting.start, dayjs(last).add(RACE_DURATION, 'minute'));
            });
        }
        days.forEach((races, i) => layoutDay(races, result[i]));
        return result;
    }, [days, calpin, showcalpin, skyracing, showskyracing, weekstart]);

    // initial scroll to the first race of the week (or 8am)
    useEffect(() =>
    {
        if (loading || !gridRef.current) return;
        const starts = days.flat().map((r) => r.start).concat(planneddays.flat().map((p) => p.start));
        const first = starts.length ? Math.min(...starts) : 8 * 60;
        gridRef.current.scrollTop = Math.max(0, (first / 60 - 0.5) * HOUR_HEIGHT);
    }, [loading, days, planneddays]);

    /* -------------------------------------------------------------------------------- *\
    |                               hoverLine
    |   horizontal line + time under the mouse (direct DOM, no re-render)
    \* -------------------------------------------------------------------------------- */
    const hoverLine = (e: React.MouseEvent<HTMLDivElement> | null) =>
    {
        const line = hoverRef.current;
        if (!line) return;
        if (!e)
        {
            line.style.display = 'none';
            return;
        }
        const y = e.clientY - e.currentTarget.getBoundingClientRect().top;
        const minutes = Math.min(Math.max(Math.floor(y / HOUR_HEIGHT * 60), 0), 24 * 60 - 1);
        line.style.display = 'block';
        line.style.top = (minutes / 60 * HOUR_HEIGHT) + 'px';
        line.firstElementChild!.textContent = String(Math.floor(minutes / 60)).padStart(2, '0') + ':' + String(minutes % 60).padStart(2, '0');
    }

    // index of today, only if the displayed week is the current week
    const todayindex = startOfWeek(now).isSame(weekstart, 'day') ? (now.day() + 6) % 7 : -1;
    const weekend = weekstart.add(6, 'day');
    const selected = dayjs(lastselecteddate);
    const isday = mode === 'day';
    const step = isday ? 1 : 7;
    // displayed days (index in the week)
    const visible = isday ? [(selected.day() + 6) % 7] : DAY_NAMES.map((_, i) => i);

    return (
        <LayoutSite>
            <div className="week">
                {loading ? <Loader /> : <></>}
                <div className='week__wrapper'>
                    <div className='week__selector'>
                        <Segmented value={mode} onChange={(v) => setMode(v as string)} options={[{ label: 'Week', value: 'week' }, { label: 'Day', value: 'day' }]} />
                        <LeftCircleFilled className='week__selector__arrow' onClick={() => changeDate((isday ? selected : weekstart).subtract(step, 'day'))} />
                        <DatePicker value={selected} onChange={(d) => d && changeDate(d)} format={'DD/MM/YYYY'} allowClear={false} />
                        <RightCircleFilled className='week__selector__arrow' onClick={() => changeDate((isday ? selected : weekstart).add(step, 'day'))} />
                        <Button type="primary" onClick={() => changeDate(dayjs())}>Today</Button>
                        <span className='week__selector__calpin'>
                            <Switch size='small' checked={showcalpin === 'true'} onChange={(v) => setShowcalpin(v ? 'true' : 'false')} /> Calpin
                        </span>
                        <span className='week__selector__calpin'>
                            <Switch size='small' checked={showskyracing === 'true'} onChange={(v) => setShowskyracing(v ? 'true' : 'false')} /> Sky Racing
                        </span>
                        <div className='week__selector__range'>{isday ? DAY_NAMES[visible[0]] + ' ' + selected.format('DD/MM/YYYY') : weekstart.format('DD/MM') + ' – ' + weekend.format('DD/MM/YYYY')}</div>
                    </div>
                </div>

                <div className='week__days'>
                    <div className='week__gutter' />
                    {
                        visible.map((i) =>
                        {
                            const d = weekstart.add(i, 'day');
                            return (
                                <div className={'week__days__day' + (i === todayindex ? ' week__days__day__today' : '')} key={'day_' + i}
                                    onClick={() => { changeDate(d); setMode(isday ? 'week' : 'day'); }}>
                                    <span className='week__days__day__name'>{DAY_NAMES[i]}</span>
                                    <span className='week__days__day__num'>{d.format('DD')}</span>
                                    <span className='week__days__day__count'>{days[i].length} races</span>
                                </div>
                            )
                        })
                    }
                </div>

                <div className='week__grid' ref={gridRef}>
                    <div className='week__grid__inner' style={{ height: 24 * HOUR_HEIGHT }} onMouseMove={hoverLine} onMouseLeave={() => hoverLine(null)}>
                        <div className='week__hover' ref={hoverRef}><span className='week__hover__time' /></div>
                        <div className='week__gutter'>
                            {
                                Array.from({ length: 24 }, (_, h) =>
                                    <div className='week__gutter__hour' key={'h_' + h} style={{ top: h * HOUR_HEIGHT }}>{h > 0 ? String(h).padStart(2, '0') + ':00' : ''}</div>
                                )
                            }
                        </div>
                        {
                            visible.map((i) => [days[i], i] as [WeekRace[], number]).map(([races, i]) =>
                                <div className={'week__col' + (i === todayindex ? ' week__col__today' : '')} key={'col_' + i} style={{ backgroundSize: `100% ${HOUR_HEIGHT}px` }}>
                                    {
                                        planneddays[i].map((p) =>
                                        {
                                            const width = isday ? `min(${100 / p.columns}%, ${DAY_MAX_WIDTH}px)` : `${100 / p.columns}%`;
                                            const offset = isday ? `(100% - ${width} * ${p.columns}) / 2` : '0px';
                                            return (
                                                <Tooltip key={p.key} color={'#333'} mouseEnterDelay={0} mouseLeaveDelay={0} title={p.tooltip}>
                                                    <div className='week__calpin'
                                                        style={{
                                                            top: p.start / 60 * HOUR_HEIGHT,
                                                            height: Math.max((p.end - p.start) / 60 * HOUR_HEIGHT - 1, 12),
                                                            left: `calc(${offset} + ${width} * ${p.column} + 1px)`,
                                                            width: `calc(${width} - 2px)`,
                                                            backgroundColor: sicColor(p.sic),
                                                            borderColor: sicColor(p.sic),
                                                        }}>
                                                        <span className='week__calpin__label'>{p.label}</span>
                                                    </div>
                                                </Tooltip>
                                            )
                                        })
                                    }
                                    {
                                        races.map((r) =>
                                        {
                                            // day mode: races capped at DAY_MAX_WIDTH and centered in the day
                                            const width = isday ? `min(${100 / r.columns}%, ${DAY_MAX_WIDTH}px)` : `${100 / r.columns}%`;
                                            const offset = isday ? `(100% - ${width} * ${r.columns}) / 2` : '0px';
                                            return (
                                                <Tooltip key={r.race.sic + '_' + r.race.number + '_' + r.race.date} color={'#333'} mouseEnterDelay={0} mouseLeaveDelay={0}
                                                    title={
                                                        <div>
                                                            <div><b>{r.race.sic} C{r.race.number}</b> – {dayjs(r.race.date).format('HH:mm')}</div>
                                                            <div>{r.race.name}</div>
                                                            <div>{r.race.distance}m – {r.race.type.toLowerCase()} – {r.race.competitors.length} runners</div>
                                                            <div>{r.race.trackname} ({r.race.source})</div>
                                                        </div>
                                                    }>
                                                    <div className='week__race'
                                                        onClick={() => openRace(r.race)}
                                                        style={{
                                                            top: r.start / 60 * HOUR_HEIGHT,
                                                            height: Math.max((r.end - r.start) / 60 * HOUR_HEIGHT - 1, 12),
                                                            left: `calc(${offset} + ${width} * ${r.column} + 1px)`,
                                                            width: `calc(${width} - 2px)`,
                                                            backgroundColor: sicColor(r.race.sic),
                                                        }}>
                                                        <span className='week__race__time'>{dayjs(r.race.date).format('HH:mm')}</span> {r.race.sic} C{r.race.number}
                                                        {isday && <span className='week__race__details'> – {r.race.name} – {r.race.distance}m – {r.race.competitors.length} runners</span>}
                                                    </div>
                                                </Tooltip>
                                            )
                                        })
                                    }
                                    {
                                        i === todayindex &&
                                        <div className='week__now' style={{ top: (now.hour() * 60 + now.minute()) / 60 * HOUR_HEIGHT }} />
                                    }
                                </div>
                            )
                        }
                    </div>
                </div>
            </div>
        </LayoutSite>
    );
}
